// web/src/features/profile/player/setupModal/hooks/useMePlayerProfile.ts
//
// Read/update a player profile for the NEW Edit Player modal via the self-scoped
// routes GET/PUT /users/me/players/:id and POST/DELETE
// /users/me/players/:id/image. Cached under `playerProfile.byId` — kept SEPARATE
// from the legacy `usePlayerProfile` hook (`player.byId`, GET /players/:id) so
// the two different endpoints/shapes don't collide or cross-invalidate.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { queryKeys } from '@sportninja/common/config/queryKeys'
import {
  getMePlayerProfile,
  updateMePlayerProfile,
  uploadMePlayerImage,
  deleteMePlayerImage,
  type PlayerProfileResponse,
  type UpdatePlayerPayload,
} from '@sportninja/common/services/player/playerProfile'

const STALE_TIME = 5 * 60 * 1000

/** GET /users/me/players/:id — read hook for the Edit Player modal. */
export const useMePlayerProfile = (playerId: string | null | undefined) => {
  const { data, isPending, isFetching, error, refetch } =
    useQuery<PlayerProfileResponse>({
      queryKey: queryKeys.playerProfile.byId(playerId),
      queryFn: () => getMePlayerProfile(playerId as string),
      enabled: Boolean(playerId),
      staleTime: STALE_TIME,
    })

  return {
    data: data ?? null,
    loading: Boolean(playerId) && (isPending || isFetching),
    error: error as Error | null,
    refetch,
  }
}

/**
 * PUT /users/me/players/:id — runs LAST in the save flow, so its invalidation
 * refreshes everything (fields + any image change). Invalidates the edit
 * profile cache and the "my family" list the profile/family screens read.
 */
export const useUpdateMePlayerProfile = () => {
  const queryClient = useQueryClient()
  return useMutation<
    PlayerProfileResponse,
    Error,
    { playerId: string; payload: UpdatePlayerPayload }
  >({
    mutationFn: ({ playerId, payload }) =>
      updateMePlayerProfile(playerId, payload),
    // Override the global mutations.retry=1 default (see queryClient config).
    retry: 0,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.playerProfile.byId(variables.playerId),
      })
      queryClient.invalidateQueries({
        queryKey: queryKeys.userFamily.me(),
      })
    },
  })
}

/**
 * POST/DELETE /users/me/players/:id/image. Invalidates the edit-profile cache
 * and the "my family" list on success so the new/removed avatar shows everywhere
 * — even on an image-only save (no player PUT runs in that case).
 */
export const useMePlayerImageMutation = () => {
  const queryClient = useQueryClient()
  const invalidate = (playerId: string) => {
    queryClient.invalidateQueries({
      queryKey: queryKeys.playerProfile.byId(playerId),
    })
    queryClient.invalidateQueries({ queryKey: queryKeys.userFamily.me() })
  }
  const upload = useMutation<
    unknown,
    Error,
    { playerId: string; file: File | string }
  >({
    mutationFn: ({ playerId, file }) => uploadMePlayerImage(playerId, file),
    // Override the global mutations.retry=1 default (see queryClient config).
    retry: 0,
    onSuccess: (_d, { playerId }) => invalidate(playerId),
  })
  const remove = useMutation<unknown, Error, { playerId: string }>({
    mutationFn: ({ playerId }) => deleteMePlayerImage(playerId),
    // Override the global mutations.retry=1 default (see queryClient config).
    retry: 0,
    onSuccess: (_d, { playerId }) => invalidate(playerId),
  })
  return {
    uploadImage: upload.mutateAsync,
    deleteImage: remove.mutateAsync,
    isPending: upload.isPending || remove.isPending,
  }
}
